/**
 * ReportViewer Component
 * Displays AI-generated analysis report for a completed survey
 * Admin-only view showing automation opportunities, redundancies, and insights
 */

import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import SurveyNotes from './SurveyNotes';
import '../styles/report.css';

/**
 * Get auth headers for API calls
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

const BULK_REGEN_POLL_INTERVAL_MS = 2000;
const BULK_REGEN_MAX_ATTEMPTS = 300;
const BULK_REGEN_FINAL_STATUSES = new Set(['completed', 'partial', 'failed']);

const parseApiErrorMessage = (payload, fallbackMessage) => {
    if (!payload) return fallbackMessage;
    if (typeof payload.detail === 'string') return payload.detail;
    if (typeof payload.message === 'string') return payload.message;
    if (typeof payload.error === 'string') return payload.error;
    if (payload.detail && typeof payload.detail.message === 'string') return payload.detail.message;
    return fallbackMessage;
};

/**
 * Source Reference Popup Component
 * Shows the original question and answer when a source reference is clicked
 */
const SourceReferencePopup = ({ questionNumber, surveyResponses, onClose }) => {
    // Find the response for this question number
    const response = surveyResponses?.find(r => r.question_number === questionNumber);
    
    if (!response) {
        return (
            <div className="source-popup-overlay" onClick={onClose}>
                <div className="source-popup" onClick={(e) => e.stopPropagation()}>
                    <div className="source-popup-header">
                        <h4>Question {questionNumber}</h4>
                        <button className="popup-close" onClick={onClose}>×</button>
                    </div>
                    <div className="source-popup-content">
                        <p className="no-data">Response not found</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="source-popup-overlay" onClick={onClose}>
            <div className="source-popup" onClick={(e) => e.stopPropagation()}>
                <div className="source-popup-header">
                    <h4>📌 Source: Question {questionNumber}</h4>
                    <button className="popup-close" onClick={onClose}>×</button>
                </div>
                <div className="source-popup-content">
                    <div className="source-question">
                        <strong>Question:</strong>
                        <p>{response.question_text}</p>
                    </div>
                    <div className="source-answer">
                        <strong>Employee Response:</strong>
                        <p>"{response.answer_text}"</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Source References Component
 * Displays clickable source reference badges
 */
const SourceReferences = ({ references, surveyResponses }) => {
    const [activePopup, setActivePopup] = useState(null);
    
    if (!references || references.length === 0) return null;
    
    return (
        <div className="source-references">
            <span className="source-label">📎 Sources:</span>
            {references.map((qNum, idx) => (
                <button 
                    key={idx}
                    className="source-badge"
                    onClick={() => setActivePopup(qNum)}
                    title={`View response to Question ${qNum}`}
                >
                    Q{qNum}
                </button>
            ))}
            {activePopup !== null && (
                <SourceReferencePopup 
                    questionNumber={activePopup}
                    surveyResponses={surveyResponses}
                    onClose={() => setActivePopup(null)}
                />
            )}
        </div>
    );
};

/**
 * Priority badge component
 */
const PriorityBadge = ({ priority }) => {
    const colors = {
        high: '#FF3B30',
        medium: '#FF9500',
        low: '#34C759'
    };
    return (
        <span 
            className="priority-badge"
            style={{ backgroundColor: colors[priority] || '#86868B' }}
        >
            {priority?.toUpperCase() || 'N/A'}
        </span>
    );
};

/**
 * Confidence badge component — color-coded 0-100 score
 */
const ConfidenceBadge = ({ score, rationale }) => {
    if (score == null) return null;
    const getColor = (s) => {
        if (s >= 80) return '#34C759';
        if (s >= 60) return '#FF9500';
        if (s >= 40) return '#FF6B35';
        return '#FF3B30';
    };
    const getLabel = (s) => {
        if (s >= 80) return 'High';
        if (s >= 60) return 'Medium';
        if (s >= 40) return 'Low';
        return 'Very Low';
    };
    return (
        <span 
            className="confidence-badge"
            style={{ 
                backgroundColor: getColor(score), 
                color: '#FFF', 
                padding: '2px 8px', 
                borderRadius: '12px', 
                fontSize: '0.75em', 
                fontWeight: 600,
                marginLeft: '6px',
                cursor: rationale ? 'help' : 'default'
            }}
            title={rationale || `Confidence: ${score}%`}
        >
            {getLabel(score)} ({score}%)
        </span>
    );
};

/**
 * Human review banner — shows when items are flagged for review
 */
const HumanReviewBanner = ({ confidenceSummary }) => {
    if (!confidenceSummary) return null;
    const flaggedItems = confidenceSummary.items_flagged_for_review || [];
    const overallConfidence = confidenceSummary.overall_confidence;
    
    if (flaggedItems.length === 0 && (overallConfidence == null || overallConfidence >= 60)) return null;
    
    const highSeverity = flaggedItems.filter(f => f.flag_severity === 'high');
    
    const sectionLabels = {
        jd_alignment: 'JD Alignment',
        time_allocation: 'Time Allocation',
        key_insights: 'Key Insights',
        automation_opportunities: 'Automation',
        process_overlaps: 'Process Overlap',
        handoff_map: 'Handoff Map',
        technology_assessment: 'Technology',
        executive_summary: 'Executive Summary',
    };
    
    return (
        <div className="human-review-banner" style={{
            background: highSeverity.length > 0 ? '#FFF3F0' : '#FFF8E1',
            border: `1px solid ${highSeverity.length > 0 ? '#FF3B30' : '#FFB300'}`,
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '16px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.95em' }}>
                    {highSeverity.length > 0 ? '⚠️' : '🔍'} Human Review Recommended
                </span>
                {overallConfidence != null && (
                    <ConfidenceBadge score={overallConfidence} rationale={confidenceSummary.overall_confidence_rationale} />
                )}
            </div>
            {confidenceSummary.overall_confidence_rationale && (
                <p style={{ fontSize: '0.85em', color: '#555', margin: '4px 0 10px', lineHeight: '1.5' }}>
                    {confidenceSummary.overall_confidence_rationale}
                </p>
            )}
            {flaggedItems.length > 0 && (
                <div style={{ fontSize: '0.85em', marginTop: '8px' }}>
                    <strong>{flaggedItems.length} item{flaggedItems.length > 1 ? 's' : ''} flagged:</strong>
                    <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none' }}>
                        {flaggedItems.map((flag, idx) => (
                            <li key={idx} style={{ 
                                marginBottom: '10px',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                backgroundColor: flag.flag_severity === 'high' ? 'rgba(255,59,48,0.06)' : 'rgba(255,179,0,0.08)',
                                borderLeft: `3px solid ${flag.flag_severity === 'high' ? '#FF3B30' : '#FFB300'}`
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                    <span style={{
                                        display: 'inline-block',
                                        fontSize: '0.75em',
                                        fontWeight: 600,
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        backgroundColor: flag.flag_severity === 'high' ? '#FF3B30' : '#FF9500',
                                        color: '#fff',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em'
                                    }}>
                                        {sectionLabels[flag.section] || flag.section.replace(/_/g, ' ')}
                                    </span>
                                    <span style={{
                                        fontSize: '0.7em',
                                        color: flag.flag_severity === 'high' ? '#CC0000' : '#996600',
                                        fontWeight: 500
                                    }}>
                                        {flag.flag_severity === 'high' ? '● High' : '● Medium'}
                                    </span>
                                </div>
                                <div style={{ color: '#1D1D1F', lineHeight: '1.45' }}>
                                    {flag.item_summary}
                                </div>
                                <div style={{ color: '#6E6E73', fontSize: '0.92em', marginTop: '4px', fontStyle: 'italic', lineHeight: '1.4' }}>
                                    {flag.flag_reason}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

/**
 * Alignment badge component - supports 5-tier scoring
 */
const AlignmentBadge = ({ alignment }) => {
    const colors = {
        aligned: '#34C759',
        partially_aligned: '#FF9500',
        misaligned_role_drift: '#FF3B30',
        misaligned_tm_understanding: '#AF52DE',
        not_mentioned: '#86868B',
        // Legacy support
        misaligned: '#FF3B30',
        different: '#FF3B30'
    };
    const labels = {
        aligned: 'Aligned',
        partially_aligned: 'Partial',
        misaligned_role_drift: 'Role Drift',
        misaligned_tm_understanding: 'TM Understanding Gap',
        not_mentioned: 'Not Discussed',
        // Legacy support
        misaligned: 'Misaligned',
        different: 'Different'
    };
    return (
        <span 
            className="alignment-badge"
            style={{ backgroundColor: colors[alignment] || '#86868B' }}
        >
            {labels[alignment] || alignment || 'Unknown'}
        </span>
    );
};

/**
 * Misalignment source tag - shows the source of misalignment
 */
const MisalignmentSourceTag = ({ source }) => {
    if (!source || source === 'not_applicable') return null;
    const sourceLabels = {
        true_role_drift: '⚠️ True Role Drift — JD update may be needed',
        tm_understanding_gap: '💡 TM Understanding Gap — work IS in JD under broader language',
        semantic_overlap: '🔗 Semantic Match — different words, same duties',
        partial_scope: '📊 Partial Scope — handles part of this JD item'
    };
    const sourceColors = {
        true_role_drift: '#FFF3F0',
        tm_understanding_gap: '#F5F0FF',
        semantic_overlap: '#F0F8FF',
        partial_scope: '#FFF8F0'
    };
    const borderColors = {
        true_role_drift: '#FF3B30',
        tm_understanding_gap: '#AF52DE',
        semantic_overlap: '#007AFF',
        partial_scope: '#FF9500'
    };
    return (
        <div className="misalignment-source-tag" style={{
            backgroundColor: sourceColors[source] || '#F5F5F5',
            border: `1px solid ${borderColors[source] || '#DDD'}`,
            borderRadius: '6px',
            padding: '4px 8px',
            fontSize: '0.8em',
            marginTop: '4px'
        }}>
            {sourceLabels[source] || source}
        </div>
    );
};

/**
 * Time allocation chart component
 */
const TimeAllocationChart = ({ data }) => {
    if (!data || data.length === 0) return <p className="no-data">No time allocation data available</p>;
    
    // Sort by percentage descending
    const sorted = [...data].sort((a, b) => b.percentage - a.percentage);
    
    return (
        <div className="time-allocation-chart">
            {sorted.map((item, idx) => (
                <div key={idx} className="time-bar-container">
                    <div className="time-bar-label">
                        <span className="category-name">{item.category}</span>
                        <span className="percentage">{item.percentage}%</span>
                    </div>
                    <div className="time-bar-wrapper">
                        <div 
                            className={`time-bar automation-${item.automation_potential}`}
                            style={{ width: `${item.percentage}%` }}
                        />
                    </div>
                    <div className="time-bar-description">
                        {item.description}
                        {item.automation_potential !== 'none' && (
                            <span className={`automation-tag ${item.automation_potential}`}>
                                {item.automation_potential} automation potential
                            </span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

/**
 * Main ReportViewer component
 */
function ReportViewer({ sessionId, session, onClose }) {
    const [report, setReport] = useState(null);
    const [surveyResponses, setSurveyResponses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && onClose) onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Helper: dismiss when clicking overlay background
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && onClose) onClose();
    };

    // Load report data and survey responses
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                
                // Load report and survey details in parallel
                const [reportResponse, detailsResponse] = await Promise.all([
                    fetch(
                        `${API_BASE_URL}/api/admin/report/${sessionId}`,
                        { headers: getAuthHeaders() }
                    ),
                    fetch(
                        `${API_BASE_URL}/api/admin/sessions/${sessionId}/full`,
                        { headers: getAuthHeaders() }
                    )
                ]);
                
                // Handle report response
                if (reportResponse.status === 404) {
                    setReport(null);
                } else if (reportResponse.ok) {
                    const data = await reportResponse.json();
                    setReport(data);
                } else {
                    throw new Error('Failed to load report');
                }
                
                // Handle survey details response (for source references)
                if (detailsResponse.ok) {
                    const details = await detailsResponse.json();
                    setSurveyResponses(details.responses || []);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [sessionId]);

    const fetchLatestReport = async () => {
        const reportResponse = await fetch(
            `${API_BASE_URL}/api/admin/report/${sessionId}`,
            { headers: getAuthHeaders() }
        );
        const reportPayload = await reportResponse.json().catch(() => null);

        if (!reportResponse.ok) {
            throw new Error(parseApiErrorMessage(reportPayload, 'Report generated, but loading it failed.'));
        }

        setReport(reportPayload);
    };

    const runSingleSessionRegenerationJob = async () => {
        const startResponse = await fetch(
            `${API_BASE_URL}/api/admin/reports/bulk-regenerate`,
            {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ session_ids: [sessionId] })
            }
        );
        const startPayload = await startResponse.json().catch(() => null);

        if (!startResponse.ok) {
            throw new Error(parseApiErrorMessage(startPayload, 'Failed to start report generation job.'));
        }

        const jobId = startPayload?.job_id;
        if (!jobId) {
            throw new Error(parseApiErrorMessage(startPayload, 'Report generation job did not return a job ID.'));
        }

        for (let attempt = 0; attempt < BULK_REGEN_MAX_ATTEMPTS; attempt += 1) {
            const statusResponse = await fetch(
                `${API_BASE_URL}/api/admin/reports/bulk-regenerate/status/${jobId}`,
                { headers: getAuthHeaders() }
            );
            const statusPayload = await statusResponse.json().catch(() => null);

            if (!statusResponse.ok) {
                throw new Error(parseApiErrorMessage(statusPayload, 'Failed to check report generation status.'));
            }

            if (BULK_REGEN_FINAL_STATUSES.has(statusPayload?.status)) {
                const sessionResult = Array.isArray(statusPayload.results)
                    ? statusPayload.results.find((item) => String(item.session_id) === String(sessionId))
                    : null;

                if (statusPayload.status === 'failed') {
                    throw new Error(
                        sessionResult?.error ||
                        parseApiErrorMessage(statusPayload, 'Report generation job failed.')
                    );
                }

                if (sessionResult?.status === 'failed') {
                    throw new Error(sessionResult.error || 'Report generation failed for this survey.');
                }

                if (sessionResult?.status === 'success' || !sessionResult) {
                    await fetchLatestReport();
                    return;
                }

                throw new Error('Report generation completed without a final result for this survey.');
            }

            await new Promise((resolve) => setTimeout(resolve, BULK_REGEN_POLL_INTERVAL_MS));
        }

        throw new Error('Report generation is taking longer than expected. Please check back in a moment and refresh.');
    };

    /**
     * Generate report if it doesn't exist
     */
    const handleGenerateReport = async () => {
        try {
            setGenerating(true);
            setError(null);

            await runSingleSessionRegenerationJob();
        } catch (err) {
            setError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    /**
     * Regenerate report (delete old and create new)
     */
    const handleRegenerateReport = async () => {
        if (!window.confirm('Are you sure you want to regenerate this report? The current report will be replaced.')) {
            return;
        }
        
        try {
            setGenerating(true);
            setError(null);

            await runSingleSessionRegenerationJob();
        } catch (err) {
            setError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    /**
     * Export report as JSON
     */
    const handleExportReport = () => {
        if (!report) return;
        
        const dataStr = JSON.stringify(report.report_data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-${session?.employee_name?.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Render loading state
    if (loading) {
        return (
            <div className="report-viewer-overlay" onClick={handleOverlayClick}>
                <div className="report-viewer-modal">
                    <div className="report-header">
                        <h2>Loading Report...</h2>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                    <div className="report-loading">
                        <div className="spinner"></div>
                        <p>Loading report...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Render no report state
    if (!report) {
        return (
            <div className="report-viewer-overlay" onClick={handleOverlayClick}>
                <div className="report-viewer-modal">
                    <div className="report-header">
                        <h2>AI Analysis Report</h2>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                    <div className="report-no-data">
                        <div className="no-report-icon">📊</div>
                        <h3>No Report Available</h3>
                        <p>
                            {session?.status === 'completed' 
                                ? 'This survey is completed but no report has been generated yet.'
                                : 'Reports can only be generated for completed surveys.'}
                        </p>
                        {session?.status === 'completed' && (
                            <button 
                                className="generate-report-btn"
                                onClick={handleGenerateReport}
                                disabled={generating}
                            >
                                {generating ? (
                                    <>
                                        <span className="btn-spinner"></span>
                                        Generating Report...
                                    </>
                                ) : (
                                    '🤖 Generate AI Report'
                                )}
                            </button>
                        )}
                        {error && <p className="error-message">{error}</p>}
                    </div>
                </div>
            </div>
        );
    }

    const reportData = report.report_data || {};
    const employeeInfo = reportData.employee_info || {};

    return (
        <div className="report-viewer-overlay" onClick={handleOverlayClick}>
            <div className="report-viewer-modal large">
                {/* Header */}
                <div className="report-header">
                    <div className="report-header-info">
                        <h2>🤖 AI Analysis Report</h2>
                        <div className="employee-badge">
                            <strong>{employeeInfo.name}</strong>
                            <span>{employeeInfo.role_title}</span>
                            <span className="dept-tag">{employeeInfo.department}</span>
                        </div>
                    </div>
                    <div className="report-header-actions">
                        <button className="regenerate-btn" onClick={handleRegenerateReport} disabled={generating} title="Regenerate this report">
                            {generating ? <Loader2 size={14} className="report-action-spinner" /> : <RefreshCw size={14} />}
                            {generating ? 'Regenerating...' : 'Regenerate'}
                        </button>
                        <button className="export-btn" onClick={handleExportReport}>
                            📥 Export
                        </button>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                </div>

                {error && <p className="error-message" style={{ padding: '10px 20px', margin: 0 }}>{error}</p>}

                {/* Tabs */}
                <div className="report-tabs" role="tablist">
                    <button 
                        id="tab-overview"
                        role="tab"
                        className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Overview
                    </button>
                    <button 
                        id="tab-time"
                        role="tab"
                        className={`tab ${activeTab === 'time' ? 'active' : ''}`}
                        onClick={() => setActiveTab('time')}
                    >
                        Time Analysis
                    </button>
                    <button 
                        id="tab-opportunities"
                        role="tab"
                        className={`tab ${activeTab === 'opportunities' ? 'active' : ''}`}
                        onClick={() => setActiveTab('opportunities')}
                    >
                        Opportunities
                    </button>
                    <button 
                        id="tab-streamlining"
                        role="tab"
                        className={`tab ${activeTab === 'streamlining' ? 'active' : ''}`}
                        onClick={() => setActiveTab('streamlining')}
                    >
                        Process Improvements
                    </button>
                    <button 
                        id="tab-insights"
                        role="tab"
                        className={`tab ${activeTab === 'insights' ? 'active' : ''}`}
                        onClick={() => setActiveTab('insights')}
                    >
                        Insights
                    </button>
                    <button 
                        id="tab-technology"
                        role="tab"
                        className={`tab ${activeTab === 'technology' ? 'active' : ''}`}
                        onClick={() => setActiveTab('technology')}
                    >
                        Technology
                    </button>
                </div>

                {/* Tab Content */}
                <div className="report-content">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="tab-content overview">
                            {/* Human Review Banner */}
                            <HumanReviewBanner confidenceSummary={reportData.confidence_summary} />

                            {/* Executive Summary */}
                            <div className="report-section summary-section">
                                <h3>📋 Executive Summary</h3>
                                <p className="executive-summary">{reportData.executive_summary}</p>
                            </div>

                            {/* Quick Stats */}
                            <div className="quick-stats">
                                <div className="stat-card">
                                    <div className="stat-icon">💡</div>
                                    <div className="stat-value">{reportData.improvement_opportunities?.length || reportData.automation_opportunities?.length || 0}</div>
                                    <div className="stat-label">Improvement Opportunities</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon">🔄</div>
                                    <div className="stat-value">{reportData.process_overlaps?.length || reportData.redundancies?.length || 0}</div>
                                    <div className="stat-label">Process Improvements</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon">🔗</div>
                                    <div className="stat-value">{reportData.handoff_map?.length || 0}</div>
                                    <div className="stat-label">Handoff Points</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon">✨</div>
                                    <div className="stat-value">{(Array.isArray(reportData.key_insights) ? reportData.key_insights : []).length}</div>
                                    <div className="stat-label">Key Insights</div>
                                </div>
                            </div>

                            {/* Key Insights Preview */}
                            {((Array.isArray(reportData.key_insights) ? reportData.key_insights : [])).length > 0 && (
                                <div className="report-section">
                                    <h3>✨ Key Insights</h3>
                                    <ul className="insights-list">
                                        {(Array.isArray(reportData.key_insights) ? reportData.key_insights : []).slice(0, 5).map((item, idx) => {
                                            const insightText = typeof item === 'string' ? item : item?.insight;
                                            const refs = item?.source_references;
                                            const confidence = typeof item === 'object' ? item?.confidence_score : null;
                                            const confidenceRationale = typeof item === 'object' ? item?.confidence_rationale : null;
                                            return (
                                                <li key={idx}>
                                                    {insightText}
                                                    <ConfidenceBadge score={confidence} rationale={confidenceRationale} />
                                                    <SourceReferences references={refs} surveyResponses={surveyResponses} />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            {/* JD Alignment Summary */}
                            {reportData.jd_alignment?.length > 0 && (
                                <div className="report-section">
                                    <h3>📄 Job Description Alignment</h3>
                                    <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '12px' }}>
                                        Each JD line item is analyzed for alignment. <strong>Role Drift</strong> = work genuinely outside JD scope. 
                                        <strong>TM Understanding Gap</strong> = work that IS covered by JD language but may not be recognized as such.
                                    </p>
                                    <div className="alignment-summary">
                                        {reportData.jd_alignment.map((item, idx) => (
                                            <div key={idx} className="alignment-item" style={{ marginBottom: '16px', padding: '12px', border: '1px solid #E5E5E5', borderRadius: '8px' }}>
                                                <div className="alignment-header">
                                                    <span className="responsibility">{item.jd_responsibility}</span>
                                                    <AlignmentBadge alignment={item.alignment_score} />
                                                    <ConfidenceBadge score={item.confidence_score} rationale={item.confidence_rationale} />
                                                </div>
                                                {item.jd_source_text && (
                                                    <div style={{ fontSize: '0.8em', color: '#888', fontStyle: 'italic', margin: '4px 0' }}>
                                                        JD text: "{item.jd_source_text}"
                                                    </div>
                                                )}
                                                <div className="alignment-details">
                                                    {item.actual_work_described && (
                                                        <p style={{ margin: '4px 0', fontSize: '0.9em' }}>
                                                            <strong>Employee described:</strong> {item.actual_work_described}
                                                        </p>
                                                    )}
                                                    <span>Actual time: {item.actual_time_spent}</span>
                                                    {item.alignment_rationale && (
                                                        <p style={{ margin: '4px 0', fontSize: '0.9em', color: '#555' }}>
                                                            <strong>Rationale:</strong> {item.alignment_rationale}
                                                        </p>
                                                    )}
                                                    <MisalignmentSourceTag source={item.misalignment_source} />
                                                    {item.misalignment_detail && (
                                                        <p style={{ margin: '4px 0', fontSize: '0.85em', color: '#444' }}>
                                                            {item.misalignment_detail}
                                                        </p>
                                                    )}
                                                    {item.suggested_followup && (
                                                        <div style={{ margin: '6px 0', padding: '6px 10px', backgroundColor: '#FFF9E6', borderRadius: '4px', fontSize: '0.85em', borderLeft: '3px solid #FFD60A' }}>
                                                            💬 <strong>Suggested follow-up:</strong> {item.suggested_followup}
                                                        </div>
                                                    )}
                                                    {item.notes && <p className="notes">{item.notes}</p>}
                                                    {item.needs_human_review && (
                                                        <div style={{ margin: '6px 0', padding: '4px 8px', backgroundColor: '#FFF3F0', borderRadius: '4px', fontSize: '0.8em', borderLeft: '3px solid #FF3B30', color: '#CC0000' }}>
                                                            ⚠️ <strong>Flagged for human review</strong> — confidence below threshold
                                                        </div>
                                                    )}
                                                    <SourceReferences references={item.source_references} surveyResponses={surveyResponses} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Time Analysis Tab */}
                    {activeTab === 'time' && (
                        <div className="tab-content time">
                            <div className="report-section">
                                <h3>⏱️ Time Allocation Breakdown</h3>
                                <TimeAllocationChart data={reportData.time_allocation} />
                            </div>

                            {/* Bottlenecks */}
                            {reportData.bottlenecks?.length > 0 && (
                                <div className="report-section">
                                    <h3>🚧 Bottlenecks Identified</h3>
                                    <div className="bottlenecks-list">
                                        {reportData.bottlenecks.map((item, idx) => (
                                            <div key={idx} className="bottleneck-card">
                                                <h4>{item.description}</h4>
                                                <p><strong>Impact:</strong> {item.impact}</p>
                                                {(item.suggested_improvement || item.suggested_resolution) && (
                                                    <p><strong>Suggested Resolution:</strong> {item.suggested_improvement || item.suggested_resolution}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Handoff Map */}
                            {reportData.handoff_map?.length > 0 && (
                                <div className="report-section">
                                    <h3>🔗 Handoff Map</h3>
                                    <table className="handoff-table">
                                        <thead>
                                            <tr>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Frequency</th>
                                                <th>Pain Points</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.handoff_map.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.from_role}</td>
                                                    <td>{item.to_role}</td>
                                                    <td>{item.frequency}</td>
                                                    <td>{item.pain_points || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Opportunities Tab (replaces Automation) */}
                    {activeTab === 'opportunities' && (
                        <div className="tab-content opportunities">
                            <div className="report-section">
                                <h3>💡 Improvement Opportunities</h3>
                                <p className="section-description">
                                    These are areas where small changes could make your work easier and free up time for more impactful activities.
                                </p>
                                {(reportData.improvement_opportunities?.length || reportData.automation_opportunities?.length) > 0 ? (
                                    <div className="opportunity-cards">
                                        {(reportData.improvement_opportunities || reportData.automation_opportunities).map((item, idx) => (
                                            <div key={idx} className="opportunity-card">
                                                <div className="opportunity-header">
                                                    <h4>{item.task}</h4>
                                                    <div>
                                                        <PriorityBadge priority={item.priority} />
                                                        <ConfidenceBadge score={item.confidence_score} rationale={item.confidence_rationale} />
                                                    </div>
                                                </div>
                                                <div className="opportunity-details">
                                                    <div className="detail-row">
                                                        <span className="label">Current Time:</span>
                                                        <span className="value">{item.current_time_spent}</span>
                                                    </div>
                                                    <div className="detail-row">
                                                        <span className="label">Potential Time Savings:</span>
                                                        <span className="value savings">{item.time_saving_potential || item.estimated_time_savings}</span>
                                                    </div>
                                                </div>
                                                <div className="solution">
                                                    <strong>Suggested Approach:</strong>
                                                    <p>{item.potential_solution}</p>
                                                </div>
                                                <SourceReferences references={item.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="no-data">No improvement opportunities identified yet</p>
                                )}
                            </div>

                            {/* Tool Recommendations */}
                            {(reportData.tool_recommendations?.length || reportData.ga_recommendations?.length) > 0 && (
                                <div className="report-section">
                                    <h3>🛠️ Tool Recommendations</h3>
                                    <p className="section-description">
                                        These tools could help simplify your workflows:
                                    </p>
                                    <div className="tool-recommendations">
                                        {(reportData.tool_recommendations || reportData.ga_recommendations).map((item, idx) => (
                                            <div key={idx} className="tool-card">
                                                <div className="tool-type">{item.tool_type}</div>
                                                <p><strong>Use Case:</strong> {item.use_case}</p>
                                                <p><strong>Expected Benefit:</strong> {item.expected_benefit}</p>
                                                <span className={`complexity ${item.implementation_complexity}`}>
                                                    {item.implementation_complexity} to implement
                                                </span>
                                                <SourceReferences references={item.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Process Improvements Tab (replaces Redundancy) */}
                    {activeTab === 'streamlining' && (
                        <div className="tab-content streamlining">
                            <div className="report-section">
                                <h3>🔄 Process Streamlining Opportunities</h3>
                                <p className="section-description">
                                    These are areas where processes could be consolidated or simplified across the team.
                                </p>
                                {(reportData.process_overlaps?.length || reportData.redundancies?.length) > 0 ? (
                                    <div className="streamline-cards">
                                        {(reportData.process_overlaps || reportData.redundancies).map((item, idx) => (
                                            <div key={idx} className="streamline-card">
                                                <h4>🔄 {item.task}</h4>
                                                <p><strong>Involved Parties:</strong> {item.involved_parties}</p>
                                                <div className="recommendation">
                                                    <strong>Recommendation:</strong>
                                                    <p>{item.recommendation}</p>
                                                </div>
                                                <SourceReferences references={item.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-streamline">
                                        <div className="success-icon">✨</div>
                                        <h4>Looking Good!</h4>
                                        <p>No major process overlaps were identified. Workflows appear to be well-organized.</p>
                                    </div>
                                )}
                            </div>

                            {/* Workflow Challenges */}
                            {(reportData.workflow_challenges?.length || reportData.bottlenecks?.length) > 0 && (
                                <div className="report-section">
                                    <h3>🚧 Workflow Challenges</h3>
                                    <div className="challenge-cards">
                                        {(reportData.workflow_challenges || reportData.bottlenecks).map((item, idx) => (
                                            <div key={idx} className="challenge-card">
                                                <h4>{item.description}</h4>
                                                <p><strong>Impact:</strong> {item.impact}</p>
                                                <p><strong>Suggested Improvement:</strong> {item.suggested_improvement || item.suggested_resolution}</p>
                                                <SourceReferences references={item.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Insights Tab */}
                    {activeTab === 'insights' && (
                        <div className="tab-content insights">
                            {/* All Key Insights */}
                            <div className="report-section">
                                <h3>✨ All Key Insights</h3>
                                {((Array.isArray(reportData.key_insights) ? reportData.key_insights : [])).length > 0 ? (
                                    <ul className="insights-list full">
                                        {(Array.isArray(reportData.key_insights) ? reportData.key_insights : []).map((item, idx) => {
                                            const insightText = typeof item === 'string' ? item : item?.insight;
                                            const refs = item?.source_references;
                                            const confidence = typeof item === 'object' ? item?.confidence_score : null;
                                            const confidenceRationale = typeof item === 'object' ? item?.confidence_rationale : null;
                                            return (
                                                <li key={idx}>
                                                    {insightText}
                                                    <ConfidenceBadge score={confidence} rationale={confidenceRationale} />
                                                    <SourceReferences references={refs} surveyResponses={surveyResponses} />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p className="no-data">No key insights available</p>
                                )}
                            </div>

                            {/* Discussion Points */}
                            {reportData.discussion_points?.length > 0 && (
                                <div className="report-section">
                                    <h3>💬 Suggested Discussion Points</h3>
                                    <p className="section-description">
                                        These topics may be valuable to discuss in 1:1 meetings:
                                    </p>
                                    <ul className="discussion-list">
                                        {reportData.discussion_points.map((item, idx) => {
                                            const topicText = typeof item === 'string' ? item : item?.topic;
                                            const refs = item?.source_references;
                                            return (
                                                <li key={idx}>
                                                    {topicText}
                                                    <SourceReferences references={refs} surveyResponses={surveyResponses} />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            {/* Team Notes Section */}
                            <div className="report-section">
                                <SurveyNotes 
                                    sessionId={sessionId}
                                    employeeName={employeeInfo.name}
                                    referenceType="report"
                                    referenceId="analysis"
                                />
                            </div>

                            {/* Report Metadata */}
                            <div className="report-section metadata">
                                <h3>📋 Report Information</h3>
                                <div className="metadata-grid">
                                    <div>
                                        <span className="label">Generated:</span>
                                        <span>{new Date(reportData.metadata?.generated_at || report.created_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })}</span>
                                    </div>
                                    <div>
                                        <span className="label">Survey Completed:</span>
                                        <span>{employeeInfo.survey_completed_at ? new Date(employeeInfo.survey_completed_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }) : 'N/A'}</span>
                                    </div>
                                    <div>
                                        <span className="label">Report Version:</span>
                                        <span>{reportData.metadata?.version || '1.0'}</span>
                                    </div>
                                    {reportData.confidence_summary?.overall_confidence != null && (
                                        <div>
                                            <span className="label">Overall Confidence:</span>
                                            <ConfidenceBadge score={reportData.confidence_summary.overall_confidence} rationale={reportData.confidence_summary.overall_confidence_rationale} />
                                        </div>
                                    )}
                                    {reportData.confidence_summary?.data_quality_factors && (
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <span className="label">Data Quality:</span>
                                            <span style={{ fontSize: '0.85em' }}>
                                                Response Detail: {reportData.confidence_summary.data_quality_factors.response_detail_level || 'N/A'} | 
                                                JD Available: {reportData.confidence_summary.data_quality_factors.jd_availability || 'N/A'} | 
                                                Consistency: {reportData.confidence_summary.data_quality_factors.response_consistency || 'N/A'} | 
                                                Coverage: {reportData.confidence_summary.data_quality_factors.coverage_completeness || 'N/A'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Technology Tab */}
                    {activeTab === 'technology' && (
                        <div className="tab-content technology">
                            {/* Overall Tech Assessment */}
                            {reportData.technology_assessment?.tech_stack_assessment && (
                                <div className="report-section summary-section">
                                    <h3>🖥️ Technology Assessment Summary</h3>
                                    <p className="summary-text">{reportData.technology_assessment.tech_stack_assessment}</p>
                                </div>
                            )}

                            {/* Tools in Use */}
                            {reportData.technology_assessment?.tools_in_use?.length > 0 && (
                                <div className="report-section">
                                    <h3>🔧 Tools Currently in Use</h3>
                                    <div className="tools-grid">
                                        {reportData.technology_assessment.tools_in_use.map((tool, idx) => (
                                            <div key={idx} className="tool-card">
                                                <div className="tool-header">
                                                    <h4>{tool.tool_name}</h4>
                                                    <span className={`satisfaction-badge ${tool.satisfaction}`}>
                                                        {tool.satisfaction === 'positive' ? '👍' : tool.satisfaction === 'negative' ? '👎' : '😐'} {tool.satisfaction}
                                                    </span>
                                                </div>
                                                <p><strong>Used:</strong> {tool.frequency}</p>
                                                <p><strong>Purpose:</strong> {tool.purpose}</p>
                                                <SourceReferences references={tool.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tools Working Well */}
                            {reportData.technology_assessment?.tools_working_well?.length > 0 && (
                                <div className="report-section">
                                    <h3>✅ Tools Working Well</h3>
                                    <p className="section-description">These tools are valued and should be kept:</p>
                                    <div className="good-tools-list">
                                        {reportData.technology_assessment.tools_working_well.map((tool, idx) => (
                                            <div key={idx} className="good-tool-item">
                                                <h4>👍 {tool.tool_name}</h4>
                                                <p>{tool.positive_aspects}</p>
                                                <SourceReferences references={tool.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tools Causing Friction */}
                            {reportData.technology_assessment?.tools_causing_friction?.length > 0 && (
                                <div className="report-section">
                                    <h3>⚠️ Tools Causing Friction</h3>
                                    <p className="section-description">These tools are slowing work down or frustrating employees:</p>
                                    <div className="friction-tools-list">
                                        {reportData.technology_assessment.tools_causing_friction.map((tool, idx) => (
                                            <div key={idx} className="friction-tool-item">
                                                <h4>🚨 {tool.tool_name}</h4>
                                                <p><strong>Issues:</strong> {tool.issues}</p>
                                                {tool.workarounds && <p><strong>Workarounds:</strong> {tool.workarounds}</p>}
                                                <p><strong>Impact:</strong> {tool.impact}</p>
                                                <SourceReferences references={tool.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tool Wishes */}
                            {reportData.technology_assessment?.tool_wishes?.length > 0 && (
                                <div className="report-section">
                                    <h3>💡 Tool Wishes & Requests</h3>
                                    <p className="section-description">Tools the employee wishes they had:</p>
                                    <div className="tool-wishes-list">
                                        {reportData.technology_assessment.tool_wishes.map((wish, idx) => (
                                            <div key={idx} className="tool-wish-item">
                                                <div className="wish-header">
                                                    <h4>💫 {wish.desired_tool}</h4>
                                                    {wish.priority && <PriorityBadge priority={wish.priority} />}
                                                </div>
                                                <p><strong>Would solve:</strong> {wish.problem_it_would_solve}</p>
                                                <SourceReferences references={wish.source_references} surveyResponses={surveyResponses} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No Technology Data Message */}
                            {(!reportData.technology_assessment || 
                              (!reportData.technology_assessment.tools_in_use?.length && 
                               !reportData.technology_assessment.tools_working_well?.length && 
                               !reportData.technology_assessment.tools_causing_friction?.length && 
                               !reportData.technology_assessment.tool_wishes?.length)) && (
                                <div className="report-section">
                                    <p className="no-data">No technology assessment data available. This may be from an older survey that didn't include technology questions.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ReportViewer;
